'use client';

import { DocumentCheckIcon } from '@heroicons/react/24/outline';
import { ScanSearch } from 'lucide-react';
import { useMemo } from 'react';
import Button from '~/components/Button';
import Chip from '~/components/Chip';
import Skeleton from '~/components/Loading/Skeleton';
import PendingLink from '~/components/Navigation/PendingLink';
import { SettingsEmptyState } from '~/components/settings/SettingsEmptyState';
import { Routes } from '~/config';
import { useWorkspace } from '~/contexts/WorkspaceContext';
import { api, skipBatchOption } from '~/utils/api';
import { formatScanHistory } from '../../content-scanner/_components/ScanHistory';

type HomeRecentScansProps = {
  enabled: boolean;
  hideHeader?: boolean;
  canCreateJob?: boolean;
  onNewJob?: () => void;
};

const LIMIT = 8;

function RecentScansSkeleton() {
  return (
    <div
      className="border-border bg-card overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
      aria-hidden
    >
      <div className="w-full overflow-x-auto px-3 pt-2 pb-1">
        <table className="w-full table-fixed border-collapse text-left">
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <thead>
            <tr className="text-muted-foreground text-[13px]">
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Report</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Brand</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Unresolved</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Last scan</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, i) => (
              <tr key={i} className="border-border/50 border-t">
                <td className="px-2 py-3.5">
                  <Skeleton className="h-4 w-40" />
                </td>
                <td className="px-2 py-3.5">
                  <Skeleton className="h-4 w-16" />
                </td>
                <td className="px-2 py-3.5">
                  <Skeleton className="h-5 w-8" />
                </td>
                <td className="px-2 py-3.5">
                  <Skeleton className="h-4 w-20" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function HomeRecentScans({
  enabled,
  hideHeader,
  canCreateJob,
  onNewJob,
}: HomeRecentScansProps) {
  const { workspaceId } = useWorkspace();

  const { data: scanHistory, isLoading } = api.scanner.getScanHistory.useQuery(
    {
      skip: 0,
      limit: LIMIT,
      filters: { brandIds: [] },
    },
    {
      ...skipBatchOption,
      enabled,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  const rows = useMemo(
    () => formatScanHistory(scanHistory?.reports || []),
    [scanHistory?.reports],
  );

  if (!enabled) return null;

  const viewAllHref = `/${workspaceId}${Routes.CONTENT_SCANNER}/dashboard`;

  const body = isLoading ? (
    <RecentScansSkeleton />
  ) : rows.length === 0 ? (
    <SettingsEmptyState
      size="section"
      icon={ScanSearch}
      title="No Scan Results Yet"
      description="Run a scan job to populate this list."
      action={
        canCreateJob && onNewJob ? (
          <Button size="sm" label="New scan job" onClick={onNewJob} />
        ) : undefined
      }
      className="min-h-0 py-4"
    />
  ) : (
    <div className="border-border bg-card overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <div className="w-full overflow-x-auto px-3 pt-2 pb-1">
        <table className="w-full table-fixed border-collapse text-left">
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <thead>
            <tr className="text-muted-foreground text-[13px]">
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Report</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Brand</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Unresolved</th>
              <th className="w-1/4 px-2 pt-1 pb-3 font-medium">Last scan</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const unresolved = row.policyFeedbackCount + row.claimCount + row.trademarkCount;
              const href = `/${workspaceId}${Routes.CONTENT_SCANNER}/brand-reports/${row.brandId}/all-reports?reportId=${row.id}`;
              const brandLabel =
                row.brandName && row.brandName !== row.brandId ? row.brandName : '—';

              return (
                <tr
                  key={row.id}
                  className="border-border/50 hover:bg-muted border-t transition-colors"
                >
                  <td className="overflow-hidden px-2 py-3.5 align-middle">
                    <PendingLink
                      href={href}
                      className="text-foreground focus-visible:ring-ring/50 inline-flex max-w-full items-center gap-1.5 text-sm font-medium outline-none focus-visible:ring-[3px]"
                    >
                      <span className="truncate">{row.name}</span>
                      {row.isReviewed && (
                        <DocumentCheckIcon
                          height={14}
                          strokeWidth={2}
                          className="text-muted-foreground shrink-0"
                          aria-label="Reviewed"
                        />
                      )}
                    </PendingLink>
                  </td>
                  <td className="overflow-hidden px-2 py-3.5 align-middle">
                    <span className="text-muted-foreground block truncate text-sm">
                      {brandLabel}
                    </span>
                  </td>
                  <td className="px-2 py-3.5 align-middle">
                    <Chip
                      level={unresolved > 0 ? 'alert' : 'success'}
                      text={unresolved}
                      className="px-2 py-0.5 text-xs tabular-nums"
                    />
                  </td>
                  <td className="overflow-hidden px-2 py-3.5 align-middle">
                    <span className="text-muted-foreground block truncate text-sm">
                      {row.lastScanAt || '—'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (hideHeader) return body;

  return (
    <section aria-labelledby="home-recent-scans-heading">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2
          id="home-recent-scans-heading"
          className="text-foreground text-sm font-semibold tracking-tight"
        >
          Recent Scans
        </h2>
        <PendingLink
          href={viewAllHref}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 text-xs outline-none focus-visible:ring-[3px]"
        >
          View all
        </PendingLink>
      </div>
      {body}
    </section>
  );
}
